import { useState, type FormEvent } from 'react';
import { contactApi } from '@/lib/api';
import './Contact.css';

type Status = { state: 'idle' | 'sending' | 'sent' } | { state: 'error'; message: string };

export default function Contact() {
  const [status, setStatus] = useState<Status>({ state: 'idle' });

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const val = (k: string) => String(f.get(k) ?? '').trim();
    setStatus({ state: 'sending' });
    try {
      await contactApi.send({
        first: val('first'),
        last: val('last'),
        phone: val('phone'),
        email: val('email'),
        company: val('company'),
        source: val('source'),
        message: val('message'),
        consent: f.get('sms') === 'on',
        website: val('website'),
      });
      setStatus({ state: 'sent' });
    } catch (err) {
      setStatus({ state: 'error', message: (err as Error).message });
    }
  };

  return (
    <main id="top">

      <section className="ct-hero">
        <div className="wrap-wide">
          <span className="eyebrow center">Contact us</span>
          <h1>Let's get your federal journey started.</h1>
          <p className="sub">Tell us about your business and a specialist will respond with clear next steps — no obligation, no jargon. Most inquiries are answered within one business day.</p>
        </div>
      </section>

      <section>
        <div className="wrap-wide">
          <div className="ct-layout">

            <div className="ct-info">
              <div className="ct-card">
                <span className="ic"><svg className="ico"><use href="#i-phone" /></svg></span>
                <div><div className="k">Call us</div><div className="v"><a href="tel:+17044598642">(704) 459-8642</a></div></div>
              </div>
              <div className="ct-card">
                <span className="ic"><svg className="ico"><use href="#i-mail" /></svg></span>
                <div><div className="k">Email</div><div className="v"><a href="mailto:presales@atzsolutionllc.com">presales@atzsolutionllc.com</a></div></div>
              </div>
              <div className="ct-card">
                <span className="ic"><svg className="ico"><use href="#i-mappin" /></svg></span>
                <div><div className="k">Headquarters</div><div className="v">301 S McDowell Street, Suite 125<br />Charlotte, NC 28204</div></div>
              </div>
              <div className="ct-card">
                <span className="ic"><svg className="ico"><use href="#i-clock" /></svg></span>
                <div><div className="k">Hours</div><div className="v">Monday – Friday<br />9:00 AM – 6:00 PM ET</div></div>
              </div>
              <div className="ct-map"><iframe title="ATZ Solution LLC office location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3259.535899230345!2d-80.84013752423289!3d35.218027472740246!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88569fa32feacf61%3A0x8edcdb951826a166!2sATZ%20Solution%20LLC!5e0!3m2!1sen!2sbd!4v1782355153343!5m2!1sen!2sbd" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" /></div>
            </div>

            <div className="ct-form">
              <h2>Send us a message</h2>
              <p className="fhint">Fields marked with <span style={{ color: 'var(--red)' }}>*</span> are required.</p>
              {status.state === 'sent' ? (
                <div className="form-sent" role="status">
                  <h3>Thank you — your message is on its way.</h3>
                  <p>A specialist will get back to you within one business day. Need us sooner? Call <a href="tel:+17044598642">(704) 459-8642</a>.</p>
                </div>
              ) : (
              <form onSubmit={onSubmit}>
                <div className="hp" aria-hidden="true"><label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" /></label></div>
                <div className="fgrid">
                  <div className="field"><label>First name <span className="req">*</span></label><input type="text" name="first" placeholder="Jane" required /></div>
                  <div className="field"><label>Last name <span className="req">*</span></label><input type="text" name="last" placeholder="Doe" required /></div>
                  <div className="field"><label>Business phone <span className="req">*</span></label><input type="tel" name="phone" placeholder="(704) 000-0000" required /></div>
                  <div className="field"><label>Email <span className="req">*</span></label><input type="email" name="email" placeholder="jane@company.com" required /></div>
                  <div className="field full"><label>Business name</label><input type="text" name="company" placeholder="Your company, LLC" /></div>
                  <div className="field full"><label>How did you hear about us?</label>
                    <select name="source" defaultValue="">
                      <option value="" disabled>Select an option</option>
                      <option>Google / search</option>
                      <option>Referral</option>
                      <option>Social media</option>
                      <option>LinkedIn</option>
                      <option>Event or webinar</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="field full"><label>How can we help? <span className="req">*</span></label><textarea name="message" placeholder="Tell us about your business and what you're trying to accomplish…" required></textarea></div>
                  <div className="consent">
                    <input type="checkbox" id="sms" name="sms" />
                    <label htmlFor="sms">I agree to receive calls, texts, and emails from ATZ Solution about my inquiry. Message and data rates may apply. Consent is not a condition of purchase.</label>
                  </div>
                </div>
                {status.state === 'error' && <p className="form-error" role="alert">{status.message}</p>}
                <button className="btn btn-primary" type="submit" disabled={status.state === 'sending'}>
                  {status.state === 'sending' ? 'Sending…' : <>Send Message <span className="arw">→</span></>}
                </button>
                <p className="privacy">By submitting, you agree to our Privacy Policy. We never share your information.</p>
              </form>
              )}
            </div>

          </div>
        </div>
      </section>

    </main>
  );
}
